import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { IDataverseQuote, IQuoteLineItem, ISubQuoteProduct, QuoteStatus } from "@/features/quotes-management/types";
import { getSubQuoteProductsApi } from '@/features/quotes-management/api/dataverse';
import { formatDealerAmount } from '@/utils/currency';
import dayjs from 'dayjs';
import logo from '@/assets/images/victorian-sliders-logo.jpg';

// Export Order Acknowledgement PDF generator
export { generateOrderAcknowledgementPDF } from './orderAcknowledgementPDF';

const getSubProductOptionType = (p: ISubQuoteProduct): number | null => {
  const raw = p.dogma_optiontype;
  if (raw !== undefined && raw !== null) {
    const n = Number(raw);
    if (!isNaN(n)) return n;
  }
  for (const field of [p.dogma_valuejson, p.dogma_value]) {
    try {
      const parsed = JSON.parse(field || '');
      if (parsed?.optionType !== undefined) return Number(parsed.optionType);
    } catch {}
  }
  if (p.dogma_optionname === 'Variation') return 64;
  return null;
};

const filterVariationItems = (items: ISubQuoteProduct[]): ISubQuoteProduct[] => {
  const hasVariation = items.some(p => getSubProductOptionType(p) === 64);
  return items.filter(p => {
    const ot = getSubProductOptionType(p);
    // When variation exists: show variation (64), hide window type (0)
    // When no variation: show window type (0) only
    if (ot === 0 && hasVariation) return false;
    return true;
  });
};

export const generateQuoteInvoicePDF = async (
  quote: IDataverseQuote,
  lineItems: IQuoteLineItem[],
  returnBase64: boolean = false,
  simplified: boolean = false,
): Promise<string | void> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  let yPosition = 15;

  // Add Logo
  try {
    doc.addImage(logo, "JPEG", 20, yPosition, 40, 20);
  } catch (error) {
  }

  yPosition += 22;

  // Invoice Title & Details (right-aligned, starts at same y as logo)
  doc.setFontSize(18);
  doc.setTextColor(40, 40, 40);
  const docTitle = quote.statuscode === QuoteStatus.CONVERTED ? 'INVOICE' : 'QUOTATION';
  doc.text(docTitle, pageWidth - 20, yPosition, { align: "right" });

  yPosition += 7;
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text(
    `Quote #: ${quote.quotenumber || "N/A"}`,
    pageWidth - 20,
    yPosition,
    { align: "right" },
  );

  yPosition += 5;
  const quoteDate = quote.createdon
    ? dayjs(quote.createdon).format("DD-MM-YYYY")
    : "N/A";
  doc.text(`Date: ${quoteDate}`, pageWidth - 20, yPosition, { align: "right" });

  yPosition += 5;
  const statusLabel =
    quote["statuscode@OData.Community.Display.V1.FormattedValue"] || "N/A";
  doc.text(`Status: ${statusLabel}`, pageWidth - 20, yPosition, {
    align: "right",
  });

  // Customer Details Section
  yPosition += 10;
  doc.setFontSize(11);
  doc.setTextColor(40, 40, 40);
  doc.text("Bill To:", 20, yPosition);

  yPosition += 6;
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);

  const customerName =
    quote.dogma_customer?.name ||
    quote["_dogma_customer_value@OData.Community.Display.V1.FormattedValue"] ||
    quote["_customerid_value@OData.Community.Display.V1.FormattedValue"] ||
    "N/A";
  doc.text(customerName, 20, yPosition);

  const addrParts = (quote.dogma_customer?.address1_composite || '')
    .split(/\r?\n/)
    .map(s => s.trim())
    .filter(Boolean);
  for (const part of addrParts) {
    yPosition += 5;
    doc.text(part, 20, yPosition);
  }

  yPosition += 5;
  const customerEmail = quote?.customerid_contact?.emailaddress1 ?? "N/A";
  doc.text(`Email: ${customerEmail}`, 20, yPosition);

  yPosition += 5;
  const customerPhone = quote?.customerid_contact?.telephone1 ?? "N/A";
  doc.text(`Phone: ${customerPhone}`, 20, yPosition);

  // Project Information
  yPosition += 5;
  const projectType =
    quote?.["dogma_projecttype@OData.Community.Display.V1.FormattedValue"] ||
    "N/A";
  doc.text(`Project Type: ${projectType}`, 20, yPosition);

  yPosition += 5;
  const projectRef = quote?.dogma_projectreference || "N/A";
  doc.text(`Project Reference: ${projectRef}`, 20, yPosition);

  // Quote Items Table
  yPosition += 8;

  const dealersCurrency = quote.dogma_DealersCurrency;
  const exchangeRate =
    quote.dogma_exchangerate ?? dealersCurrency?.dogma_exchangerate ?? 1;
  const fmt = (v: number) =>
    formatDealerAmount(v, dealersCurrency, exchangeRate);

  if (lineItems.length > 0) {
    const tableData: any[] = [];

    if (simplified) {
      // Simplified mode: one row per line item — name, qty, unit price, total
      for (const lineItem of lineItems) {
        const itemName =
          lineItem.dogma_windowname ||
          lineItem.quotedetailname ||
          lineItem.productname ||
          "Untitled Product";
        const qty = lineItem.quantity ?? 1;
        const lineTotal =
          lineItem.dogma_vsssubtotal ?? lineItem.baseamount ?? 0;
        const unitPrice = qty > 1 ? lineTotal / qty : lineTotal;
        tableData.push([
          { content: itemName, styles: { fontStyle: "bold", fontSize: 11 } },
          { content: String(qty), styles: { halign: "center" } },
          { content: fmt(unitPrice), styles: { halign: "right" } },
          { content: fmt(lineTotal), styles: { halign: "right" } },
        ]);
      }
    } else {
      // Full mode: fetch sub-products and show per-window breakdown with customer pricing
      for (const lineItem of lineItems) {
        const itemName =
          lineItem.dogma_windowname ||
          lineItem.quotedetailname ||
          lineItem.productname ||
          "Untitled Product";
        const lineQty = lineItem.quantity ?? 1;
        const lineTotal =
          lineItem.dogma_vsssubtotal ?? lineItem.baseamount ?? 0;
        const windowUnitPrice = lineQty > 1 ? lineTotal / lineQty : lineTotal;

        try {
          const subProductsResponse = await getSubQuoteProductsApi(
            lineItem.quotedetailid,
          );
          const allSubProducts: ISubQuoteProduct[] =
            subProductsResponse?.value || [];

          const subProducts = filterVariationItems(
            allSubProducts.filter(
              (item) => (item.dogma_vssprice ?? item.dogma_amount) > 0,
            ),
          );
          const regularItems = subProducts.filter(
            (item) =>
              item.dogma_categoryname !== "Surcharge" &&
              item.dogma_categoryname !== "Additional Charges",
          );
          const surgeItems = subProducts.filter(
            (item) =>
              item.dogma_categoryname === "Surcharge" ||
              item.dogma_categoryname === "Additional Charges",
          );

          // Window header row — unit price per window + total
          tableData.push([
            { content: itemName, styles: { fontStyle: "bold", fontSize: 11 } },
            {
              content: String(lineQty),
              styles: { halign: "center", fontStyle: "bold" },
            },
            {
              content: fmt(windowUnitPrice),
              styles: { fontStyle: "bold", halign: "right" },
            },
            {
              content: fmt(lineTotal),
              styles: { fontStyle: "bold", halign: "right" },
            },
          ]);

          regularItems.forEach((subProduct) => {
            const categoryLabel =
              subProduct.dogma_categoryname || "Configuration";
            const unitPrice =
              subProduct.dogma_vssprice ?? subProduct.dogma_amount;
            tableData.push([
              {
                content: `  ${categoryLabel} - ${subProduct.dogma_name}`,
                styles: { fontSize: 9, textColor: [100, 100, 100] },
              },
              {
                content: String(lineQty),
                styles: {
                  fontSize: 9,
                  textColor: [100, 100, 100],
                  halign: "center",
                },
              },
              {
                content: fmt(unitPrice),
                styles: {
                  fontSize: 9,
                  textColor: [100, 100, 100],
                  halign: "right",
                },
              },
              {
                content: fmt(unitPrice * lineQty),
                styles: {
                  fontSize: 9,
                  textColor: [100, 100, 100],
                  halign: "right",
                },
              },
            ]);
          });

          if (surgeItems.length > 0) {
            tableData.push([
              {
                content: "PRICE SURGES",
                styles: {
                  fontSize: 9,
                  fontStyle: "bold",
                  textColor: [255, 140, 0],
                },
              },
              "",
              "",
              "",
            ]);
            surgeItems.forEach((surgeItem) => {
              const isPerFrame = (surgeItem.dogma_quantity ?? 1) > 1;
              const surgeUnitPrice =
                surgeItem.dogma_vssprice ?? surgeItem.dogma_amount;
              // Flat surges (dogma_quantity === 1) are one-off — qty = 1, no lineQty multiplication
              const surgeDisplayQty = isPerFrame ? lineQty : 1;
              const surgeLineTotal = isPerFrame
                ? surgeUnitPrice * lineQty
                : surgeUnitPrice;
              tableData.push([
                {
                  content: `    ${surgeItem.dogma_name}`,
                  styles: { fontSize: 9, textColor: [139, 69, 19] },
                },
                {
                  content: String(surgeDisplayQty),
                  styles: {
                    fontSize: 9,
                    textColor: [139, 69, 19],
                    halign: "center",
                  },
                },
                {
                  content: fmt(surgeUnitPrice),
                  styles: {
                    fontSize: 9,
                    textColor: [139, 69, 19],
                    halign: "right",
                  },
                },
                {
                  content: fmt(surgeLineTotal),
                  styles: {
                    fontSize: 9,
                    textColor: [139, 69, 19],
                    halign: "right",
                  },
                },
              ]);
            });
          }
        } catch (error) {
          tableData.push([
            { content: itemName, styles: { fontStyle: "bold", fontSize: 11 } },
            {
              content: String(lineQty),
              styles: { halign: "center", fontStyle: "bold" },
            },
            {
              content: fmt(windowUnitPrice),
              styles: { fontStyle: "bold", halign: "right" },
            },
            {
              content: fmt(lineTotal),
              styles: { fontStyle: "bold", halign: "right" },
            },
          ]);
        }
      }
    }

    autoTable(doc, {
      startY: yPosition,
      head: [
        [
          { content: "Item", styles: { halign: "left" } },
          { content: "Qty", styles: { halign: "center" } },
          { content: "Unit Price", styles: { halign: "right" } },
          { content: "Amount", styles: { halign: "right" } },
        ],
      ],
      body: tableData,
      theme: "plain",
      headStyles: {
        fillColor: [220, 59, 65], // #DC3B41
        textColor: 255,
        fontSize: 11,
        fontStyle: "bold",
      },
      bodyStyles: {
        fontSize: 10,
        textColor: 50,
      },
      columnStyles: {
        0: { halign: "left", cellWidth: "auto" },
        1: { halign: "center", cellWidth: 20 },
        2: { halign: "right", cellWidth: 40 },
        3: { halign: "right", cellWidth: 40 },
      },
      margin: { left: 20, right: 20 },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
  }

  // Check if we need a new page
  if (yPosition > pageHeight - 60) {
    doc.addPage();
    yPosition = 20;
  }

  // Summary Section - aligned with table right edge
  const tableRightEdge = pageWidth - 20;
  const summaryWidth = 90;
  const summaryLabelX = tableRightEdge - summaryWidth;
  const summaryValueX = tableRightEdge;

  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.setFont("helvetica", "normal");

  // Subtotal — customer-facing VSS price
  const vssSubtotal = quote.dogma_vsssubtotal ?? quote.totallineitemamount ?? 0;
  const vssDiscount =
    quote.dogma_vssdiscountamount ?? quote.totaldiscountamount ?? 0;
  const vssVat = quote.dogma_vssvatamount ?? quote.totaltax ?? 0;
  const vssTotal = quote.dogma_vsstotalamount ?? quote.totalamount ?? 0;
  const vssDiscountPct = quote.dogma_vssdiscountpercentage;

  doc.text("Subtotal:", summaryLabelX, yPosition);
  doc.text(fmt(vssSubtotal), summaryValueX, yPosition, { align: "right" });
  yPosition += 8;

  // Discount (if applicable)
  if (vssDiscount > 0) {
    const discountLabel = vssDiscountPct
      ? `Discount (${vssDiscountPct}%):`
      : "Discount:";
    doc.text(discountLabel, summaryLabelX, yPosition);
    doc.setTextColor(34, 139, 34);
    doc.text(`-${fmt(vssDiscount)}`, summaryValueX, yPosition, {
      align: "right",
    });
    doc.setTextColor(60, 60, 60);
    yPosition += 8;

    const subtotalAfterDiscount = vssSubtotal - vssDiscount;
    doc.text("Subtotal After Discount:", summaryLabelX, yPosition);
    doc.text(fmt(subtotalAfterDiscount), summaryValueX, yPosition, {
      align: "right",
    });
    yPosition += 8;
  }

  // Delivery Charge (if applicable)
  const freightAmount = quote.freightamount ?? 0;
  if (freightAmount > 0) {
    doc.text("Delivery Charge:", summaryLabelX, yPosition);
    doc.text(fmt(freightAmount), summaryValueX, yPosition, { align: "right" });
    yPosition += 8;
  }

  // VAT
  doc.text(
    `VAT (${dealersCurrency?.dogma_vatpercentage ?? 20}%):`,
    summaryLabelX,
    yPosition,
  );
  doc.text(fmt(vssVat), summaryValueX, yPosition, { align: "right" });
  yPosition += 10;

  // Draw line
  doc.setDrawColor(200, 200, 200);
  doc.line(summaryLabelX, yPosition, summaryValueX, yPosition);

  yPosition += 8;

  // Total
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(40, 40, 40);
  doc.text("Total Amount:", summaryLabelX, yPosition);
  doc.text(fmt(vssTotal), summaryValueX, yPosition, { align: "right" });

  // Save or return the PDF
  const fileName = `${quote.statuscode === QuoteStatus.CONVERTED ? 'Invoice' : 'Quotation'}_${quote.quotenumber || 'N-A'}_${quoteDate}.pdf`;
  if (returnBase64) {
    return doc.output("datauristring").split(",")[1];
  }
  doc.save(fileName);
};;
